<template>
  <BaseModalEditCreate
    :mode="mode === 'view' ? 'edit' : mode"
    :is-modified="isModified"
    :title="getModalTitle()"
    :hide-save-button="!modifiedData.isFromManager || viewOnly"
    @save="onSave"
    @dismiss-changes="onDismissChanges">
    <template #form>
      <div v-if="representativeName" class="pointer-events-none flex flex-col">
        <label for="representative" class="text-sm font-medium">
          {{ $t('representative.representative') }} - {{ representativeName }}
        </label>
      </div>

      <!-- Material Alter Code Remoted Code -->
      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager || viewOnly }">
        <label for="materialAlterCodeRemotedCode" class="text-sm font-medium">{{
          $t('transactions.remoteCode')
        }}</label>
        <BaseInput
          v-model="modifiedData.materialAlterCodeRemotedCode"
          color="base"
          :disabled="viewOnly || !modifiedData.isFromManager || mode === 'assign'"
          class="w-full"
          show-error-as-tooltip
          :error="
            $v.materialAlterCodeRemotedCode.required.$invalid
              ? $v.materialAlterCodeRemotedCode.required.$message
              : $v.materialAlterCodeRemotedCode.uniqueRemoteCode.$invalid
                ? $v.materialAlterCodeRemotedCode.uniqueRemoteCode.$message
                : ''
          " />
      </div>

      <!-- Material Selection -->
      <div>
        <label class="text-sm font-medium">{{ $t('material.material') }}</label>
        <div v-if="viewOnly" class="mt-1 text-sm">
          {{ modifiedData.materialAlterCode?.material?.name || '-' }}
        </div>
        <BaseSearchDropdown
          v-else
          v-model="selectedMaterial"
          :list-items="dropdownMaterials"
          :count="materialCount"
          :label="$t('material.material')"
          :noResultsText="$t('messages.noResultsText')"
          :error="$v.materialId.required.$invalid ? $v.materialId.required.$message : ''"
          :disabled="viewOnly || !modifiedData.isFromManager"
          show-error-as-tooltip
          @clear-selection="handleMaterialSelection"
          @updateSearch="updateMaterialsSearch"
          @item-selected="handleMaterialSelection" />
      </div>

      <!-- AlterCode Name -->
      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager || viewOnly }">
        <label for="alterCodeName" class="text-sm font-medium">{{
          $t('material.alterName')
        }}</label>
        <BaseInput
          v-if="modifiedData.materialAlterCode?.alterCode"
          v-model="modifiedData.materialAlterCode.alterCode.name"
          color="base"
          :disabled="viewOnly || !modifiedData.isFromManager"
          show-error-as-tooltip
          :error="$v.alterCodeName.required.$invalid ? $v.alterCodeName.required.$message : ''"
          class="w-full" />
        <BaseInput v-else color="base" />
      </div>

      <!-- AlterCode Code -->
      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager || viewOnly }">
        <label for="alterCodeCode" class="text-sm font-medium">{{
          $t('material.alterCode')
        }}</label>
        <BaseInput
          v-if="modifiedData.materialAlterCode?.alterCode"
          v-model="modifiedData.materialAlterCode.alterCode.code"
          color="base"
          :disabled="viewOnly || !modifiedData.isFromManager"
          show-error-as-tooltip
          :error="$v.alterCodeCode.required.$invalid ? $v.alterCodeCode.required.$message : ''"
          class="w-full" />
        <BaseInput v-else color="base" />
      </div>

      <!-- AlterCode Type -->
      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager || viewOnly }">
        <label for="alterCodeType" class="text-sm font-medium">{{
          $t('material.alterCodeType')
        }}</label>
        <BaseSearchDropdown
          :list-items="dropDownAlterCodeTypes"
          v-model="selectedAlterCodeType"
          :no-results-text="$t('messages.noResultsText')"
          name="alterCodeType"
          show-error-as-tooltip
          internal-filter
          :disabled="!modifiedData.isFromManager || viewOnly"
          :error="$v.alterCodeTypeId.required.$invalid ? $v.alterCodeTypeId.required.$message : ''"
          @item-selected="handleAlterCodeTypeSelection"
          @clear-selection="handleAlterCodeTypeSelection" />
      </div>

      <!-- Measurement Unit -->
      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager || viewOnly }">
        <label for="measurementUnit" class="text-sm font-medium">{{
          $t('material.measurementUnit')
        }}</label>
        <BaseSearchDropdown
          :list-items="dropdownMeasurementUnits"
          v-model="selectedMeasurementUnit"
          :no-results-text="$t('messages.noResultsText')"
          name="measurementUnit"
          show-error-as-tooltip
          internal-filter
          :disabled="!modifiedData.isFromManager || viewOnly"
          :error="
            $v.measurementUnitId.required.$invalid ? $v.measurementUnitId.required.$message : ''
          "
          @item-selected="handleMeasurementUnitSelection"
          @clear-selection="handleMeasurementUnitSelection" />
      </div>

      <!-- IsActive Toggle -->
      <BaseToggle
        :class="{ 'pointer-events-none': !modifiedData.isFromManager || viewOnly }"
        :label="$t('baseObject.isActive')"
        name="isActive"
        :disable="!modifiedData.isFromManager || viewOnly"
        v-model="modifiedData.isActive" />
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { v4 as uuidv4 } from 'uuid';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import { storeToRefs } from 'pinia';
import { deepCopy } from '@/utils/data';
import { useLookupsStore } from '@/stores/lookups';
import BaseModalEditCreate from '../common/BaseModalEditCreate.vue';
import BaseInput from '../common/BaseInput.vue';
import BaseToggle from '../common/BaseToggle.vue';
import BaseSearchDropdown from '../common/BaseSearchDropdown.vue';
import type { DCMaterialAlterCode } from '@/types/DataCollection';
import type { SelectDropdownItem } from '@/types/Base';
import { RecordMode } from '@/types/Base';
import { getMaterialsWithConditions } from '@/services/api/materials';
import type { Material, MaterialAlterCode } from '@/types/Materials';

const { t: $t } = useI18n({ useScope: 'global' });

const emit = defineEmits(['closeModal', 'save']);

const props = withDefaults(
  defineProps<{
    mode: 'add' | 'edit' | 'view' | 'assign';
    materialAlterCode: DCMaterialAlterCode;
    existingMaterialIds: string[];
    representativeName?: string;
    viewOnly?: boolean;
    existingMaterialAlterCodes?: DCMaterialAlterCode[];
  }>(),
  {}
);

const modifiedData = ref<DCMaterialAlterCode>(
  deepCopy<DCMaterialAlterCode>(props.materialAlterCode)
);
const availableMaterials = ref<Material[]>([]);
const dropdownMaterials = ref<SelectDropdownItem[]>([]);
const materialCount = ref<number>(0);
const selectedAlterCodeType = ref<SelectDropdownItem | null>(null);
const selectedMeasurementUnit = ref<SelectDropdownItem | null>(null);
const materialSearchQuery = ref<string>('');

// Use lookups store for AlterCodeTypes and MeasurementUnits
const { dropDownAlterCodeTypes, dropdownMeasurementUnits } = storeToRefs(useLookupsStore());
const { getAlterCodeTypesLookup, getMeasurementUnitsLookup } = useLookupsStore();

const isModified = ref<boolean>(false);

watch(
  () => modifiedData.value,
  (newData) => {
    isModified.value = JSON.stringify(newData) !== JSON.stringify(props.materialAlterCode);
  },
  { deep: true }
);

// Load available materials (excluding those already mapped to this representative)
async function loadAvailableMaterials(searchQuery: string = '') {
  try {
    // Prepare excludeIDs - use existing material IDs from prop, but exclude current item when editing
    let excludeIDs = props.existingMaterialIds || [];
    if (props.mode === 'edit' && props.materialAlterCode.materialAlterCode?.materialId) {
      excludeIDs = excludeIDs.filter(
        (id) => id !== props.materialAlterCode.materialAlterCode?.materialId
      );
    }

    const materialsResponse = await getMaterialsWithConditions({
      page: 1,
      itemsPerPage: 50, // Limit for dropdown
      query: searchQuery,
      excludeIDs: excludeIDs,
    });

    availableMaterials.value = materialsResponse.data;
    materialCount.value = materialsResponse.count || materialsResponse.data.length;

    // Update dropdown items
    dropdownMaterials.value = availableMaterials.value.map((material: Material) => ({
      name: `${material.code} - ${material.name}`,
      value: material.id,
    }));
  } catch (error) {
    console.error('Error loading available materials:', error);
    availableMaterials.value = [];
    dropdownMaterials.value = [];
    materialCount.value = 0;
  }
}

function updateMaterialsSearch(searchQuery: string) {
  materialSearchQuery.value = searchQuery;
  loadAvailableMaterials(searchQuery);
}

function handleAlterCodeTypeSelection(selectedItem: SelectDropdownItem) {
  selectedAlterCodeType.value = selectedItem;
  if (selectedItem && modifiedData.value.materialAlterCode?.alterCode) {
    modifiedData.value.materialAlterCode.alterCode.alterCodeTypeId = selectedItem.value as string;
  }
  $v.value.alterCodeTypeId.$touch();
}

function handleMeasurementUnitSelection(selectedItem: SelectDropdownItem) {
  selectedMeasurementUnit.value = selectedItem;
  if (selectedItem && modifiedData.value.materialAlterCode) {
    modifiedData.value.materialAlterCode.measurementUnitId = selectedItem.value as string;
    modifiedData.value.materialAlterCode.measurementUnit = {
      id: selectedItem.value as string,
      name: selectedItem.name,
      code: '',
    };
  }
  $v.value.measurementUnitId.$touch();
}

const selectedMaterial = ref<SelectDropdownItem | null>(null);
function handleMaterialSelection(item: SelectDropdownItem) {
  selectedMaterial.value = item;
  if (item && modifiedData.value.materialAlterCode) {
    modifiedData.value.materialAlterCode.materialId = item.value as string;
    const material = availableMaterials.value.find((m) => m.id === item.value);
    if (material) {
      modifiedData.value.materialAlterCode.material = {
        id: material.id,
        name: material.name,
        code: material.code,
      };
      modifiedData.value.materialAlterCode.materialId = material.id;
    }
  } else {
    if (modifiedData.value.materialAlterCode) {
      modifiedData.value.materialAlterCode.materialId = '';
      modifiedData.value.materialAlterCode.material = undefined;
    }
  }
  $v.value.materialId.$touch();
}

function onDismissChanges() {
  modifiedData.value = deepCopy<DCMaterialAlterCode>(props.materialAlterCode);
  emit('closeModal');
}

function onSave() {
  if ($v.value.$invalid) return;
  if (
    modifiedData.value.materialAlterCode?.alterCodeId &&
    modifiedData.value.materialAlterCode.alterCode
  )
    modifiedData.value.materialAlterCode.alterCode.id =
      modifiedData.value.materialAlterCode.alterCodeId;
  if (modifiedData.value.materialAlterCode)
    modifiedData.value.materialAlterCode.id = modifiedData.value.materialAlterCodeId;

  emit('save', modifiedData.value);
}

onMounted(async () => {
  // Load lookups data
  await getAlterCodeTypesLookup();
  await getMeasurementUnitsLookup();
  await loadAvailableMaterials();

  if (!modifiedData.value) return;
  if (
    !modifiedData.value.materialAlterCodeId ||
    modifiedData.value.materialAlterCodeId == '00000000-0000-0000-0000-000000000000'
  ) {
    const newMaterialAlterCode: Partial<MaterialAlterCode> = {
      id: uuidv4(),
      isActive: true,
      isFromManager: true,
      recordMode: RecordMode.Added,
      alterCode: {
        id: uuidv4(),
        isActive: true,
        isFromManager: true,
        recordMode: RecordMode.Added,
        code: '',
        name: '',
      },
    };
    modifiedData.value.materialAlterCode = newMaterialAlterCode;
    modifiedData.value.materialAlterCodeId = newMaterialAlterCode.id;
  }
  initProperties();
});

function initProperties() {
  // Set selected material if editing existing record
  if (modifiedData.value && modifiedData.value.materialAlterCode?.materialId) {
    const material = availableMaterials.value.find(
      (m) => m.id === modifiedData.value.materialAlterCode?.materialId
    );
    if (material) {
      selectedMaterial.value = {
        name: `${material.code} - ${material.name}`,
        value: String(material.id),
      };
    }
  }

  // Set selected AlterCodeType if editing existing record
  if (modifiedData.value && modifiedData.value.materialAlterCode?.alterCode?.alterCodeTypeId) {
    const alterCodeType = dropDownAlterCodeTypes.value.find(
      (type) => type.value === modifiedData.value.materialAlterCode?.alterCode?.alterCodeTypeId
    );
    if (alterCodeType) {
      selectedAlterCodeType.value = alterCodeType;
    }
  }

  // Set selected MeasurementUnit if editing existing record
  if (modifiedData.value && modifiedData.value.materialAlterCode?.measurementUnitId) {
    const measurementUnit = dropdownMeasurementUnits.value.find(
      (unit) => unit.value === modifiedData.value.materialAlterCode?.measurementUnitId
    );
    if (measurementUnit) {
      selectedMeasurementUnit.value = measurementUnit;
    }
  }
}

const validationModel = ref({
  materialId: computed(() => modifiedData.value.materialAlterCode?.materialId),
  alterCodeName: computed(() => modifiedData.value.materialAlterCode?.alterCode?.name),
  alterCodeCode: computed(() => modifiedData.value.materialAlterCode?.alterCode?.code),
  alterCodeTypeId: computed(() => modifiedData.value.materialAlterCode?.alterCode?.alterCodeTypeId),
  measurementUnitId: computed(() => modifiedData.value.materialAlterCode?.measurementUnitId),
  materialAlterCodeRemotedCode: computed(() => modifiedData.value.materialAlterCodeRemotedCode),
});

// Custom validator for uniqueness
const isUniqueRemoteCode = (value: string) => {
  if (!value || !props.existingMaterialAlterCodes) return true;

  const existingDuplicate = props.existingMaterialAlterCodes.find(
    (mac) =>
      mac.id !== modifiedData.value.id &&
      mac.materialAlterCodeRemotedCode === value &&
      mac.recordMode !== RecordMode.Deleted
  );

  return !existingDuplicate;
};

const rules = {
  materialId: {
    required: helpers.withMessage($t('messages.errors.requiredField'), required),
  },
  alterCodeName: {
    required: helpers.withMessage($t('messages.errors.requiredField'), required),
  },
  alterCodeCode: {
    required: helpers.withMessage($t('messages.errors.requiredField'), required),
  },
  alterCodeTypeId: {
    required: helpers.withMessage($t('messages.errors.requiredField'), required),
  },
  measurementUnitId: {
    required: helpers.withMessage($t('messages.errors.requiredField'), required),
  },
  materialAlterCodeRemotedCode: {
    required: helpers.withMessage($t('messages.errors.requiredField'), required),
    uniqueRemoteCode: helpers.withMessage(
      $t('messages.errors.duplicateRemoteCode'),
      isUniqueRemoteCode
    ),
  },
};

const $v = useVuelidate(rules, validationModel);

function getModalTitle() {
  const actionKey =
    props.viewOnly || props.mode === 'view' ? 'view' : props.mode === 'add' ? 'create' : 'edit';
  return `${$t(`common.actions.${actionKey}`)} ${$t('representative.entities.materialAlterCode')}`;
}
</script>
